import { useEffect, useState, useCallback } from "react";
import { CrewEtaBanner } from "../../components/RunningLateButton";
import { useParams, Link } from "react-router-dom";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { PortalPage, Section, Pill } from "../../components/portal/PortalPage";
import { ProgressStepper } from "../../components/portal/ProgressStepper";
import { NoticeBox } from "../../components/NoticeBox";
import { AttachmentGallery } from "../../components/portal/AttachmentGallery";
import { ClientTimeline } from "../../components/portal/ClientTimeline";
import { ClientNotesThread } from "../../components/portal/ClientNotesThread";
import { VariationQuotes } from "../../components/portal/VariationQuotes";
import { ClientSignOff } from "../../components/portal/ClientSignOff";
import { usePortal } from "../../components/portal/usePortal";
import { fmtDate, fmtDateTime, fmtMoney, JOB_STATUS, INVOICE_STATUS, titleCase } from "../../components/portal/portalUtils";
import { downloadAuthFile } from "../../lib/fileOpen";
import { MapPin, Phone, Truck, FileText, FolderOpen, Receipt, ClipboardText, DownloadSimple, CalendarBlank, ChatCircle, SealCheck, ListChecks } from "@phosphor-icons/react";
import { ScopeText, scopeLines } from "../../components/jobs/ScopeOfWorksField";
import { DetailSkeleton } from "../../components/Skeletons";
import { useTeamLabel } from "../../components/portal/PortalBrand";
import { ClientSubJobs, ClientParentJobLink } from "../../components/portal/ClientSubJobs";
import BookAppointment from "../../components/portal/BookAppointment";
import { ClientRamsSection } from "../../components/portal/ClientRamsSection";
import { ClientBeforeAfter } from "../../components/portal/ClientBeforeAfter";

export default function ClientJobDetailPage() {
  const teamLabel = useTeamLabel();
  const { id } = useParams();
  const { perms } = usePortal();
  const [j, setJ] = useState(null);
  const [folder, setFolder] = useState(null);
  const load = useCallback(() => API.get(`/client-hub/jobs/${id}`).then(({ data }) => setJ(data)).catch(() => setJ(false)), [id]);
  useEffect(() => {
    load();
    API.get(`/entity-folders/job/${id}`).then(({ data }) => setFolder(data)).catch(() => setFolder(null));
  }, [id, load]);
  if (j === null) return <DetailSkeleton />;
  if (j === false) return <PortalPage title="Job not found" back={{ to: "/portal/jobs", label: "Jobs" }} />;

  const contractor = j.contractor;
  const hubFiles = folder?.files || [];

  return (
    <PortalPage
      title={j.title}
      subtitle={<span className="flex items-center gap-2 flex-wrap"><span className="font-mono">{j.reference}</span><Pill map={JOB_STATUS} value={j.status} /><ClientParentJobLink job={j} />{j.request && <Link to={`/portal/requests/${j.request.id}`} className="text-[11px] text-primary hover:underline flex items-center gap-1" data-testid="job-request-link"><ClipboardText size={11} /> From request {j.request.reference}</Link>}</span>}
      back={{ to: "/portal/jobs", label: "Jobs" }}
      testid="job-detail"
      actions={folder?.folder && <Button size="sm" variant="outline" asChild><Link to={`/portal/files/${folder.folder.id}`} data-testid="job-files-btn"><FolderOpen size={14} className="mr-1" /> Job Files</Link></Button>}
    >
      <CrewEtaBanner job={j} hideName className="mb-6" />
      <NoticeBox notice={j.notice} className="mb-6" testid="job-notice" />
      {j.parent_job && (
        <p className="mb-4 text-xs text-muted-foreground" data-testid="client-job-parent-link">
          Part of <Link to={`/portal/jobs/${j.parent_job.id}`} className="text-primary hover:underline font-medium">{j.parent_job.reference} · {j.parent_job.title}</Link>
        </p>
      )}
      <Section title="Progress" className="mb-6"><ProgressStepper progress={j.progress} /></Section>
      {(j.sub_jobs || []).length > 0 && (
        <Section title={`Work items (${j.sub_jobs.filter(s => s.status === "completed").length} of ${j.sub_jobs.filter(s => s.status !== "cancelled").length} complete)`} className="mb-6">
          <ul className="divide-y divide-border" data-testid="client-sub-jobs">
            {j.sub_jobs.map(s => (
              <li key={s.id} className="flex items-center gap-3 py-2 text-xs">
                <span className="font-mono text-muted-foreground w-28 shrink-0 truncate">{s.reference}</span>
                <Link to={`/portal/jobs/${s.id}`} className="flex-1 min-w-0 text-sm font-medium hover:underline truncate" data-testid={`client-sub-job-${s.id}`}>{s.title}</Link>
                <span className="hidden sm:block text-muted-foreground w-24">{s.scheduled_start ? fmtDate(s.scheduled_start) : "TBC"}</span>
                <Pill map={JOB_STATUS} value={s.status} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <ClientSubJobs job={j} teamLabel={teamLabel} />
          {scopeLines(j.scope_of_works).length > 0 && (
            <Section title={<span className="flex items-center gap-1.5"><ListChecks size={14} /> Scope of Works</span>} testid="job-scope-of-works">
              <ScopeText text={j.scope_of_works} testid="job-scope-of-works-text" />
            </Section>
          )}
          <Section title="Schedule & Details" action={!["completed", "cancelled"].includes(j.status) && !j.arrived_at && perms.can_request ? <BookAppointment jobId={j.id} scheduled={!!j.scheduled_start} onBooked={load} /> : null}>
            <div className="grid sm:grid-cols-3 gap-3 text-xs">
              <div className="border border-border rounded-md p-3">
                <p className="text-muted-foreground flex items-center gap-1"><CalendarBlank size={12} /> Scheduled</p>
                <p className="font-semibold mt-1">{j.scheduled_start ? fmtDate(j.scheduled_start) : "TBC"}</p>
                {j.schedule?.start_time && <p className="text-muted-foreground">{j.schedule.start_time}{j.schedule.end_time ? ` — ${j.schedule.end_time}` : ""}</p>}
              </div>
              <div className="border border-border rounded-md p-3">
                <p className="text-muted-foreground">Started</p>
                <p className="font-semibold mt-1">{j.started_at ? fmtDateTime(j.started_at) : "—"}</p>
                {j.arrived_at && <p className="text-emerald-600">Arrived {fmtDateTime(j.arrived_at).split(", ")[1]}</p>}
              </div>
              <div className="border border-border rounded-md p-3">
                <p className="text-muted-foreground">Completed</p>
                <p className="font-semibold mt-1">{j.completed_at ? fmtDateTime(j.completed_at) : "—"}</p>
              </div>
            </div>
            {j.description && <p className="text-sm mt-4 whitespace-pre-wrap">{j.description}</p>}
            {j.completion_notes && (
              <div className="mt-4 border-l-2 border-emerald-500 pl-3" data-testid="completion-notes">
                <p className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">Completion notes</p>
                <p className="text-sm whitespace-pre-wrap">{j.completion_notes}</p>
              </div>
            )}
            <dl className="grid sm:grid-cols-2 gap-3 mt-4 text-xs">
              <div><dt className="text-muted-foreground">Category</dt><dd className="font-medium">{titleCase(j.category)}</dd></div>
              <div><dt className="text-muted-foreground">Created</dt><dd className="font-medium">{fmtDate(j.created_at)}</dd></div>
            </dl>
          </Section>

          <Section title={`Photos (${(j.photos || []).length})`} testid="job-photos">
            <AttachmentGallery attachments={j.photos || []} emptyText="Photos will appear here as the contractor uploads before/after shots." />
          </Section>

          {(j.before_after_pairs || []).length > 0 && (
            <Section title={`Before & after (${j.before_after_pairs.length})`} testid="job-before-after">
              <p className="mb-3 text-xs text-muted-foreground">Each completion photo is shown beside the original it fixes — the numbered rings mark the problem areas the {teamLabel.heading.toLowerCase()} worked on.</p>
              <ClientBeforeAfter pairs={j.before_after_pairs} />
            </Section>
          )}

          <ClientRamsSection rams={j.rams} jobRef={j.reference} />

          <Section title="Completion Documents & Certificates" testid="job-documents">
            {(j.form_submissions || []).length === 0 && (j.documents || []).length === 0 && hubFiles.length === 0 ? (
              <p className="text-xs text-muted-foreground">Job sheets, certificates and completion forms will appear here once the work is signed off.</p>
            ) : (
              <div className="space-y-3">
                {(j.form_submissions || []).map((s) => (
                  <button key={s.id} onClick={() => downloadAuthFile(`/api/jobs/${id}/form-submissions/${s.id}/pdf`, `${j.reference}_${(s.form_name || "form").replace(/\s+/g, "_")}.pdf`)} className="w-full flex items-center gap-3 border border-border rounded-md p-3 hover:bg-muted/40 text-left" data-testid={`form-submission-${s.id}`}>
                    <FileText size={20} className="text-primary shrink-0" weight="duotone" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{s.form_name || "Completion form"}</p>
                      <p className="text-[10px] text-muted-foreground">Submitted {fmtDateTime(s.submitted_at || s.created_at)}{s.submitted_by_name ? ` by ${s.submitted_by_name}` : ""}</p>
                    </div>
                    <DownloadSimple size={16} className="text-muted-foreground" />
                  </button>
                ))}
                {(j.documents || []).length > 0 && <AttachmentGallery attachments={j.documents} testid="job-doc-attachments" />}
                {hubFiles.length > 0 && (
                  <AttachmentGallery attachments={hubFiles.map((f) => ({ name: f.name, url: `/api/files/${f.id}/preview`, type: f.mime_type, size: f.size }))} testid="job-hub-files" />
                )}
              </div>
            )}
          </Section>
          {(j.status === "completed" || j.client_sign_off) && (
            <Section title={<span className="flex items-center gap-1.5"><SealCheck size={14} /> Final sign-off</span>} testid="job-sign-off">
              <ClientSignOff job={j} onSigned={load} />
            </Section>
          )}

          {(j.variation_quotes || []).length > 0 && (
            <Section title={<span className="flex items-center gap-1.5"><Receipt size={14} /> Additional works quotes ({j.variation_quotes.length})</span>} testid="job-variations">
              <VariationQuotes quotes={j.variation_quotes} canApprove={perms.can_approve} onChanged={load} />
            </Section>
          )}

          <Section title={<span className="flex items-center gap-1.5"><ChatCircle size={14} /> Conversation</span>} testid="job-comments">
            <ClientNotesThread items={j.client_comments || []} postUrl={`/client-hub/jobs/${id}/comments`} folder="client-jobs" canMessage={perms.can_message} onSent={load} testPrefix="job-comment" />
          </Section>
        </div>

        <div className="space-y-6">
          <Section title={teamLabel.heading} testid="job-contractor-section">
            {contractor ? <p className="text-sm font-medium flex items-center gap-2"><Truck size={16} className="text-primary" /> {contractor}</p>
              : j.contractor_assigned ? <p className="text-sm font-medium flex items-center gap-2 text-emerald-700 dark:text-emerald-400" data-testid="job-contractor-assigned"><Truck size={16} /> {teamLabel.assigned}</p>
              : <p className="text-xs text-muted-foreground">To be confirmed.</p>}
            {j.contractor_assigned && !contractor && <p className="text-[11px] text-muted-foreground mt-1">{teamLabel.blurb}</p>}
            {(j.works_orders || []).length > 0 && <p className="text-[11px] text-muted-foreground mt-2">Works order {j.works_orders[0].reference} · {titleCase(j.works_orders[0].status)}</p>}
          </Section>
          <Section title="Property">
            <p className="text-sm font-medium flex items-start gap-1.5"><MapPin size={16} className="text-primary shrink-0 mt-0.5" /> {j.address}</p>
            {j.what3words && <p className="text-[11px] text-muted-foreground mt-1">///{j.what3words}</p>}
            {(j.site_contact_name || j.site_contact_phone) && <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Phone size={12} /> {j.site_contact_name} {j.site_contact_phone}</p>}
          </Section>
          {j.invoice && (
            <Section title="Invoice" testid="job-invoice">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono text-muted-foreground">{j.invoice.reference || j.invoice.invoice_number}</p>
                  <p className="text-lg font-bold font-[Chivo]">{fmtMoney(j.invoice.amount)}</p>
                </div>
                <Pill map={INVOICE_STATUS} value={j.invoice.status} />
              </div>
              <Link to={`/portal/invoices/${j.invoice.id}`} className="text-[11px] text-primary hover:underline mt-2 inline-flex items-center gap-1" data-testid="job-invoice-view"><Receipt size={12} /> View invoice</Link>
            </Section>
          )}
          <Section title="Activity"><ClientTimeline entries={j.timeline} /></Section>
        </div>
      </div>
    </PortalPage>
  );
}
